/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/

import PropTypes from 'prop-types';
import { Camera, Video, Hash, Upload, CheckCircle, X } from 'lucide-react';
import MediaUploader from './MediaUploader';
import { getIdentifierLabel } from '../constants/workflowOptions';

const typeIcons = {
  photo: Camera,
  video: Video,
  identifier: Hash,
  file: Upload
};

const typeLabels = {
  photo: 'Photo',
  video: 'Video',
  identifier: 'Identifier',
  file: 'File'
};

const FullScreenViewer = ({ url, type, onClose, duration = 0 }) => {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 20, right: 20, zIndex: 10000, background: 'rgba(255, 255, 255, 0.79)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black' }}
      >
        <X size={20} />
      </button>
      {type === 'video' ? (
        <video
          src={url}
          controls
          autoPlay
          onClick={e => e.stopPropagation()}
          style={{ width: '100vw', height: '100vh', objectFit: 'contain' }}
          onLoadedData={(e) => {
            e.target.currentTime = duration;
            e.target.style.opacity = 1;
          }}
        />
      ) : (
        <img src={url} alt="Full view" onClick={e => e.stopPropagation()} style={{ width: '100vw', height: '100vh', objectFit: 'contain' }} />
      )}
    </div>
  );
};

FullScreenViewer.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  onClose: PropTypes.func,
  duration: PropTypes.number,
};

const CaptureStep = ({ assignment, tenant, item, stepNumber, totalSteps, onCapture, onRetake, onBack, onNext, uploading, uploadProgress, branding, capturedData, isServerCompleted, completedMedia, token, retakeMode = false, workflow, location }) => {
  const Icon = typeIcons[item.type] || Camera;

  if (isServerCompleted) {
    // If we have completed media from server, show full-screen review via MediaUploader
    if (completedMedia) {
      const capturedForDisplay = {
        file: null,
        previewUrl: completedMedia.url,
        item: { ...item, media: completedMedia },
        identifierText: completedMedia.customerIdentifierText ?? completedMedia.identifierText ?? '',
        customerIdentifierText: completedMedia.customerIdentifierText ?? completedMedia.identifierText ?? '',
        ocrIdentifierText: completedMedia.ocrIdentifierText ?? '',
      };

      return (
        <div className="pt-1">
          <MediaUploader
            assignment={assignment}
            tenant={tenant}
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            item={item}
            onCapture={onCapture}
            onRetake={onRetake}
            onBack={onBack}
            onNext={onNext}
            branding={branding}
            captured={capturedForDisplay}
            token={token}
            retakeMode={retakeMode}
            isAlreadyUploaded={true}
            workflow={workflow}
            location={location}
          />
        </div>
      );
    }

    // Fallback for completed items without server media but with local captured data
    if (capturedData) {
      return (
        <div className="pt-1">
          <MediaUploader
            assignment={assignment}
            tenant={tenant}
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            item={item}
            onCapture={onCapture}
            onRetake={onRetake}
            onBack={onBack}
            onNext={onNext}
            branding={branding}
            captured={capturedData}
            token={token}
            retakeMode={retakeMode}
            isAlreadyUploaded={true}
            workflow={workflow}
            location={location}
          />
        </div>
      );
    }

    // Fallback for completed items without any media data
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-[40px] h-[40px] rounded-[14px] bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shadow-sm">
              <CheckCircle size={16} />
            </div>
            <div>
              <p className="text-[10.5px] font-black opacity-40 uppercase tracking-[0.16em] leading-none mb-1">Step {stepNumber}</p>
              <p className="text-[13.5px] font-bold text-base-content uppercase tracking-wider">{typeLabels[item.type]} - Completed</p>
            </div>
          </div>
          <span className="text-[10px] font-bold text-success uppercase bg-success/10 border border-success/20 px-2.5 py-1 rounded-full">
            ✓ Previously Submitted
          </span>
        </div>

        <div>
          <h2 className="text-lg font-bold text-base-content tracking-tight leading-[1.2]">{item.description}</h2>
          {item.type === 'identifier' && item.identifierFormat && (
            <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold opacity-60 uppercase tracking-widest bg-base-200 border border-base-300 px-2.5 py-1.5 rounded-full mt-2">
              <span className="opacity-70">Format:</span> {getIdentifierLabel(item.identifierFormat)}
            </div>
          )}
        </div>

        <div className="bg-success/5 flex flex-col items-center justify-center py-10 m-2 rounded-[1rem] border border-dashed border-success/20">
          <div className="w-12 h-12 bg-success/20 rounded-[14px] flex items-center justify-center mb-3">
            <CheckCircle size={28} className="text-success" />
          </div>
          <p className="text-[11px] font-black text-success uppercase tracking-[0.15em]">Previously submitted</p>
          <p className="text-xs text-success/70">This step was completed in a previous session</p>
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-3 animate-fade-in">
      {/* High-Density Status Row */}
      <div className="flex items-center justify-between px-1 border-b border-base-content/5 pb-2 mb-2">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${capturedData ? 'bg-success/10 text-success border-success/30' : 'bg-primary/10 text-primary border-primary/20'}`}>
            {capturedData ? <CheckCircle size={18} strokeWidth={2.5} /> : <Icon size={18} strokeWidth={2.5} />}
          </div>
          <div className="flex flex-col">
            <p className="text-[10px] font-black opacity-30 uppercase tracking-widest leading-none mb-1">Step {stepNumber}</p>
            <p className="text-[12px] font-extrabold uppercase tracking-widest leading-none text-base-content/70">{typeLabels[item.type]}</p>
            {item.type === 'file' && item.allowedFileType && (
              <p className="text-[10px] font-bold uppercase tracking-widest leading-none mt-1 text-base-content/50">{item.allowedFileType.toUpperCase()}</p>
            )}
          </div>
        </div>
        {item.required && (
          <span className="text-[9px] font-black text-white uppercase tracking-[0.1em] bg-error/90 px-2.5 py-0.5 rounded-full">
            Required
          </span>
        )}
      </div>

      <div className="px-1">
        <h2 className="text-[20px] font-bold tracking-tight leading-tight text-base-content/90 custom-line-break">{item.description}</h2>
        {item.type === 'identifier' && item.identifierFormat && (
          <div className="inline-flex mt-2 items-center gap-1.5 text-[10px] font-black text-primary uppercase tracking-widest bg-primary/5 px-2.5 py-1 rounded-md border border-primary/10">
            <span className="opacity-40">Format:</span> {getIdentifierLabel(item.identifierFormat)}
          </div>
        )}
      </div>

      <div className="pt-1">
        <MediaUploader
          assignment={assignment}
          tenant={tenant}
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          item={item}
          onCapture={onCapture}
          onRetake={onRetake}
          onBack={onBack}
          onNext={onNext}
          branding={branding}
          captured={capturedData}
          token={token}
          retakeMode={retakeMode}
          workflow={workflow}
          location={location}
        />
      </div>
    </div>
  );
};

export default CaptureStep;

CaptureStep.propTypes = {
  assignment: PropTypes.object,
  tenant: PropTypes.object,
  item: PropTypes.shape({
    type: PropTypes.string,
    description: PropTypes.string,
    identifierFormat: PropTypes.string,
    required: PropTypes.bool,
    allowedFileType: PropTypes.string,
  }),
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  onCapture: PropTypes.func,
  onRetake: PropTypes.func,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  branding: PropTypes.object,
  capturedData: PropTypes.object,
  isServerCompleted: PropTypes.bool,
  completedMedia: PropTypes.shape({
    url: PropTypes.string,
    identifierText: PropTypes.string,
  }),
  token: PropTypes.string,
  retakeMode: PropTypes.bool,
  workflow: PropTypes.object,
  location: PropTypes.object,
};
